import { usePrevious } from '@sequencehq/utils'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import {
  applyNodeChanges,
  applyEdgeChanges,
  addEdge,
  reconnectEdge,
  OnNodesChange,
  OnEdgesChange,
  OnConnect,
  OnConnectEnd,
  OnReconnect,
  OnNodeDrag,
  useReactFlow
} from '@xyflow/react'
import { useAtom, useAtomValue, useSetAtom, useStore } from 'jotai'

import {
  connectionDropAtom,
  connectionDropPreviewNodeAtom,
  draggingNodesAtom,
  edgesAtom,
  editingVersionIdAtom,
  isEditingDisabledAtom,
  nodesAtom,
  resetPresentationHistoryAtom,
  selectedNodeIdsAtom,
  focusedSwitchGroupAtom,
  useResolvedNodeDefinitions
} from 'Automations/automationEditor/atoms'
import { getNodeHandleId } from 'Automations/nodeDefinitions/helpers/nodeHandleId'
import { NodeType } from 'Automations/nodeDefinitions/types'
import { useNodeExpansionLayout } from './useNodeExpansionLayout'
import {
  CONNECTION_PREVIEW_NODE_ID,
  CONNECTION_PREVIEW_NODE_TYPE,
  CONNECTION_PREVIEW_SKELETON_HEIGHT,
  CONNECTION_PREVIEW_Z_INDEX
} from '../components/ReactFlow/connectionPreview.constants'

export const useCanvas = () => {
  const store = useStore()
  const { screenToFlowPosition } = useReactFlow()
  const [nodes, setNodes] = useAtom(nodesAtom)
  const [edges, setEdges] = useAtom(edgesAtom)
  const isEditingDisabled = useAtomValue(isEditingDisabledAtom)
  const [selectedNodeIds, setSelectedNodeIds] = useAtom(selectedNodeIdsAtom)
  const setDraggingNodes = useSetAtom(draggingNodesAtom)
  const editingVersionId = useAtomValue(editingVersionIdAtom)
  const resetHistory = useSetAtom(resetPresentationHistoryAtom)
  const focusedSwitchGroup = useAtomValue(focusedSwitchGroupAtom)
  const setConnectionDrop = useSetAtom(connectionDropAtom)
  const connectionDrop = useAtomValue(connectionDropAtom)
  const connectionPreviewNode = useAtomValue(connectionDropPreviewNodeAtom)
  const definitions = useResolvedNodeDefinitions()
  const isDraggingRef = useRef(false)

  const { applyRenderOffset, freezeOffsets, removeFrozenOffset } =
    useNodeExpansionLayout(nodes)

  const previousVersionEditingId = usePrevious(editingVersionId)
  useEffect(() => {
    if (editingVersionId && editingVersionId !== previousVersionEditingId) {
      resetHistory()
    }
  }, [editingVersionId, previousVersionEditingId, resetHistory])

  const onNodeDragStart: OnNodeDrag = useCallback(() => {
    isDraggingRef.current = true
    freezeOffsets()
  }, [freezeOffsets])

  const onNodeDragStop: OnNodeDrag = useCallback(() => {
    isDraggingRef.current = false
    // `nodes` comes from nodesAtom getter which reads draggingNodesAtom,
    // so it has the full node array with final drag positions (already
    // reconciled to presentation space in onNodesChange).
    // Commit before clearing the overlay to avoid a flash of stale positions.
    setNodes(nodes)
    setDraggingNodes(null)
  }, [nodes, setNodes, setDraggingNodes])

  const onNodesChange: OnNodesChange = useCallback(
    changes => {
      if (isEditingDisabled) return

      // Route selection changes through selectedNodeIdsAtom rather than
      // the presentation atom. renderedNodes merges the atom value into
      // the nodes prop. This avoids a circular dependency where
      // ConfigEditor's onSelectionChange and onNodesChange competed
      // to update ReactFlow's selection state.
      const selectionChanges = changes.filter(c => c.type === 'select')
      if (selectionChanges.length > 0) {
        setSelectedNodeIds(prev => {
          const newIds = Array.from(
            selectionChanges.reduce((acc, change) => {
              if (change.selected) acc.add(change.id)
              else acc.delete(change.id)
              return acc
            }, new Set(prev))
          )
          if (
            prev.length === newIds.length &&
            prev.every((id, i) => id === newIds[i])
          ) {
            return prev
          }
          return newIds
        })
      }

      const nonSelectionChanges = changes.filter(c => c.type !== 'select')
      if (nonSelectionChanges.length === 0) return

      // ReactFlow reports drag positions in rendered space. Subtract the frozen
      // offset of each repositioned node so what lands in the atoms is always
      // presentation space.
      const repositionedIds = new Set(
        nonSelectionChanges.flatMap(change =>
          change.type === 'position' && change.position ? [change.id] : []
        )
      )

      const updatedNodes = applyNodeChanges(nonSelectionChanges, nodes).map(
        node => (repositionedIds.has(node.id) ? removeFrozenOffset(node) : node)
      )

      if (isDraggingRef.current) {
        // During drag, update overlay atom only (no history entry)
        setDraggingNodes(updatedNodes)
      } else {
        setNodes(updatedNodes)
      }
    },
    [
      nodes,
      setNodes,
      setDraggingNodes,
      isEditingDisabled,
      setSelectedNodeIds,
      removeFrozenOffset
    ]
  )

  const onEdgesChange: OnEdgesChange = useCallback(
    changes => {
      if (isEditingDisabled) return
      // Apply against the latest edges from the store, not the render-time
      // snapshot: React Flow can fire a change (e.g. selection) right after an
      // external edit like inserting a node on an edge, and a stale snapshot
      // would clobber the freshly spliced edges.
      const updatedEdges = applyEdgeChanges(changes, store.get(edgesAtom))
      setEdges(updatedEdges)
    },
    [store, setEdges, isEditingDisabled]
  )

  const onConnect: OnConnect = useCallback(
    connection => {
      if (isEditingDisabled) return
      const updatedEdges = addEdge(connection, edges)
      setEdges(updatedEdges)
    },
    [edges, setEdges, isEditingDisabled]
  )

  const onConnectEnd: OnConnectEnd = useCallback(
    (event, connectionState) => {
      if (isEditingDisabled) return
      // A drop on a valid handle is handled by onConnect; only act on drops
      // onto empty canvas, started from a source (output) handle.
      if (connectionState.isValid) return
      const { fromNode, fromHandle } = connectionState
      if (!fromNode || fromHandle?.type !== 'source') return

      const point = 'changedTouches' in event ? event.changedTouches[0] : event
      // Place the node so its target handle — the centre of its left edge,
      // where the dropped connection lands — sits at the drop point, rather
      // than its top-left corner. The handle sits half a node-height below the
      // top, so lift the node's position by that much.
      const dropPosition = screenToFlowPosition({
        x: point.clientX,
        y: point.clientY
      })
      setConnectionDrop({
        sourceNodeId: fromNode.id,
        sourceHandleId: fromHandle.id ?? null,
        position: {
          x: dropPosition.x,
          y: dropPosition.y - CONNECTION_PREVIEW_SKELETON_HEIGHT / 2
        },
        clientX: point.clientX,
        clientY: point.clientY
      })
    },
    [isEditingDisabled, screenToFlowPosition, setConnectionDrop]
  )

  const onReconnect: OnReconnect = useCallback(
    (oldEdge, newConnection) => {
      if (isEditingDisabled) return
      const updatedEdges = reconnectEdge(oldEdge, newConnection, edges)
      setEdges(updatedEdges)
    },
    [edges, setEdges, isEditingDisabled]
  )

  const renderedNodes = useMemo(() => {
    const baseNodes = nodes.map(node => {
      if (!node.type) return node

      const position = applyRenderOffset(node, isDraggingRef.current)

      if (isEditingDisabled) {
        return {
          ...node,
          position,
          selected: false,
          deletable: false,
          selectable: false
        }
      }

      const selected = selectedNodeIds.includes(node.id)

      const definition = definitions[node.type as NodeType]
      const deletable: boolean = Boolean(definition.isPersistent) === false

      return { ...node, position, selected, deletable }
    })

    if (!connectionDrop) return baseNodes

    // While a connection is dropped on empty canvas, show a ghost target: the
    // hovered node rendered for real at 0.7 opacity, or — until something has
    // been hovered — a skeleton sized like the source node.
    const ghost = connectionPreviewNode
      ? {
          ...connectionPreviewNode,
          selectable: false,
          deletable: false,
          draggable: false,
          // Sit above existing nodes while dragging out the connection.
          zIndex: CONNECTION_PREVIEW_Z_INDEX,
          style: { opacity: 0.7, pointerEvents: 'none' as const }
        }
      : {
          id: CONNECTION_PREVIEW_NODE_ID,
          type: CONNECTION_PREVIEW_NODE_TYPE,
          position: connectionDrop.position,
          data: {},
          selectable: false,
          deletable: false,
          draggable: false,
          zIndex: CONNECTION_PREVIEW_Z_INDEX
        }

    return [...baseNodes, ghost]
  }, [
    nodes,
    applyRenderOffset,
    selectedNodeIds,
    isEditingDisabled,
    connectionDrop,
    connectionPreviewNode,
    definitions
  ])

  const switchNodeIds = useMemo(() => {
    return new Set(
      nodes.filter(node => node.type === 'SWITCH').map(node => node.id)
    )
  }, [nodes])

  /**
   * Apply the custom edge style to all edges and highlight edges
   * connected to the currently selected node(s). For switch nodes,
   * only the outgoing edge for the selected branch is highlighted.
   */
  const renderedEdges = useMemo(() => {
    const selectedSet = new Set(selectedNodeIds)

    const isSingleSwitchSelected =
      selectedNodeIds.length === 1 &&
      switchNodeIds.has(selectedNodeIds[0]) &&
      Boolean(focusedSwitchGroup)

    const focusedSwitchHandle = isSingleSwitchSelected
      ? getNodeHandleId({
          nodeId: selectedNodeIds[0],
          type: 'source',
          handleMapping: focusedSwitchGroup!
        })
      : undefined

    const baseEdges = edges.map(edge => {
      const isTargetSelected = selectedSet.has(edge.target)
      const isSourceSelected = selectedSet.has(edge.source)
      const isFromSwitch = switchNodeIds.has(edge.source)
      const isFocusedSwitchEdge =
        isSourceSelected && edge.sourceHandle === focusedSwitchHandle

      const connectedToSelectedNode = isFromSwitch
        ? isTargetSelected || isFocusedSwitchEdge
        : isSourceSelected || isTargetSelected

      return {
        ...edge,
        type: 'defaultEdge',
        zIndex: connectedToSelectedNode ? 1 : 0,
        data: { connectedToSelectedNode }
      }
    })

    if (!connectionDrop) return baseEdges

    // Always show the dragged edge feeding the ghost target (skeleton or
    // hovered node — whose id is type-specific) until the picker is dismissed.
    const ghostNodeId = connectionPreviewNode?.id ?? CONNECTION_PREVIEW_NODE_ID

    return [
      ...baseEdges,
      {
        id: 'connection-preview-edge',
        source: connectionDrop.sourceNodeId,
        sourceHandle: connectionDrop.sourceHandleId,
        target: ghostNodeId,
        targetHandle: getNodeHandleId({ nodeId: ghostNodeId, type: 'target' }),
        type: 'defaultEdge',
        selectable: false,
        // Elevate above existing nodes so the dragged edge isn't hidden behind a
        // node it's drawn over.
        zIndex: CONNECTION_PREVIEW_Z_INDEX,
        data: { connectedToSelectedNode: false, isPreview: true }
      }
    ]
  }, [
    edges,
    selectedNodeIds,
    switchNodeIds,
    focusedSwitchGroup,
    connectionDrop,
    connectionPreviewNode
  ])

  return {
    nodes: renderedNodes,
    edges: renderedEdges,
    onNodesChange,
    onEdgesChange,
    onConnect,
    onConnectEnd,
    onReconnect,
    onNodeDragStart,
    onNodeDragStop
  }
}
